import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Dropdown } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  DASHBOARD_TIME_RANGE_PRESETS,
  getTimeRangeLabel,
} from '../utils/timeRange';
import './TimeRangeSelect.css';

const { RangePicker } = DatePicker;

function TimeRangeSelect({ value, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [draftRange, setDraftRange] = useState(null);

  const selectedPeriod = value?.period || 'last7days';

  useEffect(() => {
    if (selectedPeriod === 'custom' && value?.startDate && value?.endDate) {
      setDraftRange([dayjs(value.startDate), dayjs(value.endDate)]);
    }
  }, [selectedPeriod, value?.startDate, value?.endDate]);

  const triggerLabel = useMemo(() => getTimeRangeLabel(value), [value]);

  const handlePresetSelect = (period) => {
    if (period === 'custom') {
      setShowCustomPanel(true);
      if (value?.startDate && value?.endDate) {
        setDraftRange([dayjs(value.startDate), dayjs(value.endDate)]);
      } else {
        setDraftRange([dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')]);
      }
      return;
    }
    setShowCustomPanel(false);
    setOpen(false);
    onChange?.({ period });
  };

  const handleCustomApply = () => {
    if (!draftRange?.[0] || !draftRange?.[1]) return;
    const startDate = draftRange[0].startOf('day').toISOString();
    const endDate = draftRange[1].endOf('day').toISOString();
    onChange?.({ period: 'custom', startDate, endDate });
    setShowCustomPanel(false);
    setOpen(false);
  };

  const handleCustomCancel = () => {
    setShowCustomPanel(false);
    if (selectedPeriod !== 'custom') {
      setDraftRange(null);
    }
  };

  const dropdownContent = (
    <div className="time-range-select__menu" onClick={(e) => e.stopPropagation()}>
      <div className="time-range-select__options">
        {DASHBOARD_TIME_RANGE_PRESETS.map((preset) => {
          const isActive =
            preset.value === 'custom'
              ? selectedPeriod === 'custom'
              : selectedPeriod === preset.value && !showCustomPanel;
          return (
            <button
              key={preset.value}
              type="button"
              className={`time-range-select__option${isActive ? ' is-active' : ''}`}
              onClick={() => handlePresetSelect(preset.value)}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      {showCustomPanel && (
        <div className="time-range-select__custom">
          <RangePicker
            value={draftRange}
            onChange={(dates) => setDraftRange(dates)}
            allowClear={false}
            className="time-range-select__picker"
          />
          <div className="time-range-select__custom-actions">
            <Button size="small" onClick={handleCustomCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleCustomApply}
              disabled={!draftRange?.[0] || !draftRange?.[1]}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setShowCustomPanel(selectedPeriod === 'custom');
        }
      }}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
    >
      <button
        type="button"
        className={`time-range-select__trigger${className ? ` ${className}` : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="time-range-select__label">{triggerLabel}</span>
        {open ? <UpOutlined className="time-range-select__chevron" /> : <DownOutlined className="time-range-select__chevron" />}
      </button>
    </Dropdown>
  );
}

export default TimeRangeSelect;
